/**
 * Bounded, absolute-indexed ring buffer for telemetry rows.
 *
 * Why this exists
 * ---------------
 * Every telemetry row carries a full-DTS-range array (~22,000 floats for TR/ZR/LR),
 * and the client previously retained one row per tick for the entire cure
 * (`temperatureDataRows.push(data.TRC[0])`). At ~176 KB/row/family that reaches
 * ~1.5 GB of JS heap over a 24 h cure and the tab dies before the cure ends.
 * The retained size is length-independent — the array is the fixed DTS range, so a
 * short bench fiber costs exactly as much heap as a 2 km one.
 *
 * SciChart 3.1.348 has no `fifoCapacity` on `BaseDataSeries`, so the FIFO has to
 * live in our own row storage. This is it.
 *
 * Addressing
 * ----------
 * Callers index by ABSOLUTE tick number, which never shifts as rows are evicted —
 * that keeps `tickPosition` / slider values meaningful for the whole cure. Rows
 * older than `capacity` are dropped and `get()` returns `undefined` for them; call
 * `has()` first (or handle `undefined`) and trigger a history refetch on a miss.
 */
export class TelemetryRingBuffer<T> {
  private items: (T | undefined)[];
  /** Absolute index of the oldest retained row. */
  private start = 0;
  /** Absolute index one past the newest retained row (i.e. total rows ever seen). */
  private end = 0;

  /**
   * Retention window. Not readonly: the adaptive memory cap may shrink it under
   * heap pressure (see shrinkTo).
   */
  private _capacity: number;

  constructor(capacity: number) {
    if (!Number.isInteger(capacity) || capacity < 1) {
      throw new Error(`TelemetryRingBuffer: capacity must be a positive integer, got ${capacity}`);
    }
    this._capacity = capacity;
    this.items = new Array<T | undefined>(capacity);
  }

  get capacity(): number {
    return this._capacity;
  }

  /**
   * Shrink the retention window, dropping the oldest rows.
   *
   * The memory cap's last resort, once lowering resolution is not enough. Slots are
   * addressed as `absIndex % capacity`, so changing capacity re-maps every row — the
   * buffer has to be rebuilt rather than truncated in place. Absolute indices are
   * preserved across the rebuild, which is what keeps `tickPosition` and slider
   * values meaningful; the rows that fall off simply become refetches on scrub-back,
   * exactly like ordinary eviction.
   *
   * Growing is not supported: a larger window would have holes where the dropped
   * rows were, and `has()` cannot express "retained, but missing".
   */
  shrinkTo(capacity: number): boolean {
    const target = Math.floor(capacity);
    if (!Number.isFinite(target) || target < 1 || target >= this._capacity) {
      return false;
    }

    const keepFrom = Math.max(this.start, this.end - target);
    const rebuilt = new Array<T | undefined>(target);
    for (let i = keepFrom; i < this.end; i++) {
      rebuilt[i % target] = this.items[i % this._capacity];
    }

    this.items = rebuilt;
    this._capacity = target;
    this.start = keepFrom;
    return true;
  }

  /** Absolute index of the oldest row still retained. */
  get firstIndex(): number {
    return this.start;
  }

  /** Absolute index one past the newest row — equals the total tick count ever seen. */
  get nextIndex(): number {
    return this.end;
  }

  /** Total ticks seen (including evicted ones). Mirrors the old `rows.length`. */
  get totalCount(): number {
    return this.end;
  }

  /** Number of rows currently retained in memory (<= capacity). */
  get retainedCount(): number {
    return this.end - this.start;
  }

  /** How many rows have been dropped off the back. */
  get evictedCount(): number {
    return this.start;
  }

  /** True when at least one row has been evicted (so scrub-back may miss). */
  get hasEvicted(): boolean {
    return this.start > 0;
  }

  /** True when the absolute index is still retained. */
  has(absIndex: number): boolean {
    return absIndex >= this.start && absIndex < this.end;
  }

  /** Row at an absolute tick index, or undefined when evicted / not yet seen. */
  get(absIndex: number): T | undefined {
    if (!this.has(absIndex)) {
      return undefined;
    }
    return this.items[absIndex % this.capacity];
  }

  /**
   * Newest row, or undefined when empty. Used by the RT path, which only ever
   * needs the row it just appended.
   */
  get last(): T | undefined {
    return this.end > this.start ? this.get(this.end - 1) : undefined;
  }

  /** Append one row, evicting the oldest if at capacity. */
  push(item: T): void {
    // Writing the slot IS the eviction: at capacity, `end % capacity` is the slot
    // the oldest retained row occupies, so this overwrite drops the last reference
    // to it (and to its ~22k array) and lets the GC take it. Do not null the slot
    // afterwards — `start % capacity` is that same slot.
    this.items[this.end % this.capacity] = item;
    this.end++;
    if (this.end - this.start > this.capacity) {
      this.start = this.end - this.capacity;
    }
  }

  /**
   * Replace a single retained row in place, keeping its absolute index.
   *
   * Used by the zoom refetch (telemetry item 3): the full-resolution rows fetched
   * for a zoomed window replace the decimated rows already held for those ticks.
   * Silently ignores an index that has been evicted or not yet seen — the caller
   * cannot meaningfully store a row outside the window.
   *
   * @returns true when the row was stored.
   */
  replace(absIndex: number, item: T): boolean {
    if (!this.has(absIndex)) {
      return false;
    }
    this.items[absIndex % this.capacity] = item;
    return true;
  }

  /**
   * Replace the whole buffer from a backfill response, keeping only the most
   * recent `capacity` rows. Absolute indices are preserved, so tick N in the
   * server's array is still tick N here.
   */
  reset(rows: readonly T[]): void {
    this.items = new Array<T | undefined>(this.capacity);
    this.end = rows.length;
    this.start = Math.max(0, rows.length - this.capacity);
    for (let i = this.start; i < this.end; i++) {
      this.items[i % this.capacity] = rows[i];
    }
  }

  /** Drop everything (project switch). */
  clear(): void {
    this.items = new Array<T | undefined>(this.capacity);
    this.start = 0;
    this.end = 0;
  }

  /**
   * Nearest RETAINED row to an absolute index, or undefined when empty.
   *
   * The never-blank path. Scrubbing to an evicted tick used to clear the series
   * and wait for a refetch, so the graph ERASED and redrew — the thing Joel
   * reported. Returning the closest row we still hold degrades that to
   * slightly-stale instead: the trace stays on screen, and the refetch (if any)
   * refines it. Stale-by-a-few-seconds beats blank in every case.
   */
  nearest(absIndex: number): { row: T; index: number } | undefined {
    if (this.end <= this.start) {
      return undefined;
    }
    const clamped = Math.min(Math.max(absIndex, this.start), this.end - 1);
    const row = this.items[clamped % this._capacity];
    return row === undefined ? undefined : { row, index: clamped };
  }

  /**
   * Resize the retention window, keeping the newest rows.
   *
   * Growing is safe HERE in a way it is not in general: callers resize
   * immediately before `reset()` refills from a complete backfill, so the wider
   * window is filled in the same breath and never contains holes. Shrinking
   * reuses the same rebuild as shrinkTo.
   *
   * Absolute indices are preserved either way, which is what keeps tickPosition
   * and the slider meaningful across a resize.
   */
  resizeTo(capacity: number): boolean {
    const target = Math.floor(capacity);
    if (!Number.isFinite(target) || target < 1 || target === this._capacity) {
      return false;
    }
    if (target < this._capacity) {
      return this.shrinkTo(target);
    }

    const rebuilt = new Array<T | undefined>(target);
    for (let i = this.start; i < this.end; i++) {
      rebuilt[i % target] = this.items[i % this._capacity];
    }
    this.items = rebuilt;
    this._capacity = target;
    return true;
  }

  /**
   * Retained rows, oldest-first, as a dense array. Allocates — use only where a
   * whole-window pass is genuinely required, never per-poll.
   */
  toArray(): T[] {
    const out: T[] = [];
    for (let i = this.start; i < this.end; i++) {
      const item = this.items[i % this.capacity];
      if (item !== undefined) {
        out.push(item);
      }
    }
    return out;
  }

  /** Iterate retained rows oldest-first without allocating an intermediate array. */
  forEachRetained(fn: (item: T, absIndex: number) => void): void {
    for (let i = this.start; i < this.end; i++) {
      const item = this.items[i % this.capacity];
      if (item !== undefined) {
        fn(item, i);
      }
    }
  }
}

/**
 * Default retention window, in ticks.
 *
 * Sizing math (worst case, 2 km fiber filling the fixed ~22,000-point DTS range):
 *   one row  = 22,000 doubles x 8 B  ~= 176 KB
 *   the temperature screen retains 3 buffers concurrently
 *     (temperatureDataRows + zoneDataRows + per-tick heatMap slices)
 *   so heap ~= 3 x N x 176 KB.
 *
 * N = 600 gives ~316 MB worst case, against ~1.5 GB unbounded over a 24 h cure —
 * and, critically, it is now FLAT rather than growing with cure duration. 600 ticks
 * is ~50 min at a 5 s cadence, which covers the live-scrubbing range operators
 * actually use; older ticks are refetched on demand.
 *
 * On a short bench fiber the per-row cost is identical (the array is the fixed DTS
 * range regardless of fiber length), so this bound is length-independent and the
 * behaviour is reproducible on the bench.
 */
export const DEFAULT_TICK_RETENTION = 600;

/**
 * Retention for the derived per-tick heatmap slices (zone + waterfall). Same width
 * as a raw row, so it gets the same window.
 */
export const DEFAULT_HEATMAP_RETENTION = 600;
